import { routes } from '@/constants/routes/routes';
import userManager from '@/auth';

/**
 * Thrown by `postJson`/`postMultipart` below. Carries the HTTP status code
 * alongside the parsed error body so callers can reliably branch on status
 * (e.g. detecting a corrupted DDD file upload via a 400) instead of hoping
 * the backend's JSON payload happens to include a `status` field.
 */
export class CompatibilityApiError extends Error {
  status: number;

  body: unknown;

  constructor(status: number, body: unknown) {
    let message = `HTTP ${status}`;
    if (body && typeof body === 'object' && 'message' in body) {
      const { message: bodyMessage } = body as { message?: unknown };
      if (typeof bodyMessage === 'string') {
        message = bodyMessage;
      }
    } else if (typeof body === 'string' && body.length > 0) {
      message = body;
    }

    super(message);
    this.status = status;
    this.body = body;
  }
}

const buildUrl = (path: string): string => `${routes.apiRoutes.basePath}${path}`;

const parseErrorBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type');

  try {
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    const body = await response.text();
    return body || { message: `HTTP ${response.status}: ${response.statusText}` };
  } catch (_) {
    return { message: `HTTP ${response.status}: ${response.statusText}` };
  }
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw new CompatibilityApiError(response.status, body);
  }

  return response.json() as Promise<T>;
};

/**
 * The DDD upload endpoint is not fully covered by the generated
 * `ctp-compatibility-checker` client:
 *
 * - `checkDDDCompatibility` exists in the generated client, but it sends the
 *   file as `application/x-www-form-urlencoded` instead of
 *   `multipart/form-data`, so it can't be used to upload a real DDD file.
 *   See `API_DDD_COMPATIBILITY.MD` at the repo root for the expected
 *   contract (multipart form field named `dddFile`).
 */
export const postMultipart = async <T>(path: string, formData: FormData): Promise<T> => {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: {
      Authorization: userManager.getApiKey()
    },
    body: formData
  });

  return handleResponse<T>(response);
};
